import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ExcessPaymentResponseDTO {
  id: number;
  // 🆕 RELACIÓN DUAL: Individual vs Corporativo
  enrollmentId?: number | null;
  enrollment?: {
    id: number;
    participantName: string;
    projectName: string;
    currencyId?: number;
    currencyCode?: string;
    currencySymbol?: string;
    exchangeRateUsed?: number;
  };
  corporateEnrollmentId?: number | null;
  corporateEnrollment?: {
    id: number;
    companyName: string;
    projectName: string;
    currencyId?: number;
    currencyCode?: string;
    currencySymbol?: string;
    exchangeRateUsed?: number;
  };
  // 🆕 PARTICIPANTE NULLABLE (para pagos corporativos donde paga la empresa)
  participant?: {
    id: number;
    firstName: string;
    lastName: string;
    documentId: string;
    typeDocument: string;
    expeditionPlace: string;
    profession: string;
    email: string;
    phone: string;
    cellPhone: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    country: string;
    age: number;
    status: number;
  } | null;
  // 🆕 COMPANY para excedentes corporativos
  company?: {
    id: number;
    name: string;
    documentId: string;
    typeDocument: string;
    expeditionPlace: string;
    phone: string;
    email: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    country: string;
  } | null;
  project: {
    id: number;
    projectId: string;
    name: string;
    description: string;
    period: string;
    status: string;
    date: string;
    dateClosed: string;
    isPublic: boolean;
    typeProject: string;
    duration: number;
    noParticipants: number;
    projectedParticipants: number;
    observations: string;
    inscriptionAmountCOP: number;
    totalIncome: number;
    totalExpenses: number;
    returns: number;
    projectedRevenue: number;
    projectedSpend: number;
    maxPercentDiscount: number;
  };
  bank: {
    id: number;
    accountNumber: string;
    bankName: string;
    address: string;
    branch: string;
    phone: string;
  };
  originIncomeReceipt: {
    id: number;
    receiptType: string;
    grossAmount: number;
    netAmount: number;
    paymentGroupId: string;
    receivedAmount: number;
    currency: string;
    exchangeRate: number;
    feeDetails: Array<{
      id: number;
      feeType: string;
      amount: number;
      concept: string;
    }>;
    totalFees: number;
    concept: string;
    observations?: string;
    referenceNumber: string;
    isManualPayment: boolean;
    createdAt: string;
    updatedAt: string;
  };
  excessAmount: number;
  availableAmount: number;
  usedAmount: number;
  remainingAmount: number;
  status: string;
  statusDescription: string;
  excessDate: string;
  currency?: string;
  currencyDescription?: string;
  exchangeRate: number;
  concept: string;
  observations?: string;
  paymentGroupId?: string;
  isBankReconciled: boolean;
  bankReconciliationDate?: string;
  bankReconciliationReference?: string;
  expirationDate?: string;
  processedDate?: string;
  processedBy?: string;
  isExpired: boolean;
  isFullyUsed: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  
  // 🆕 PROPIEDADES CALCULADAS PARA IDENTIFICAR TIPO
  isIndividualPayment?: boolean;
  isCorporatePayment?: boolean;
  excessPaymentType?: string;
}

export interface Result<T> {
  isSuccess: boolean;
  data: T;
  errorMessage: string | null;
  totalCount?: number;
  pageSize?: number;
  currentPage?: number;
}

// Nuevas interfaces para el resumen del grupo de pagos
export interface PaymentGroupSummaryDTO {
  paymentGroupId: string;
  totalReceivedAmount: number;
  totalAppliedAmount: number;
  totalExcessAmount: number;
  installmentsPaid: number;
  paymentDate: string;
  originalConcept: string;
  currencyCode: string;
  incomeReceipts: IncomeReceiptSummaryDTO[];
  excessPayment?: ExcessPaymentSummaryDTO;
}

export interface IncomeReceiptSummaryDTO {
  id: number;
  grossAmount: number;
  netAmount: number;
  concept: string;
  installmentNumber: number;
  paymentPlanStatus: string;
  createdAt: string;
}

export interface ExcessPaymentSummaryDTO {
  id: number;
  excessAmount: number;
  availableAmount: number;
  usedAmount: number;
  status: string;
  excessDate: string;
  expirationDate?: string;
  isExpired: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ExcessPaymentsService {
  private apiUrl = `${environment.apiUrl}/excessPayments`;

  constructor(private http: HttpClient) { }

  /**
   * Obtener todos los excedentes
   */
  getAll(): Observable<Result<ExcessPaymentResponseDTO[]>> {
    return this.http.get<Result<ExcessPaymentResponseDTO[]>>(this.apiUrl);
  }

  /**
   * Obtener excedente por ID
   */
  getById(id: number): Observable<Result<ExcessPaymentResponseDTO>> {
    return this.http.get<Result<ExcessPaymentResponseDTO>>(`${this.apiUrl}/${id}`);
  }

  /**
   * Obtener excedentes por participante
   */
  getByParticipant(participantId: number): Observable<Result<ExcessPaymentResponseDTO[]>> {
    return this.http.get<Result<ExcessPaymentResponseDTO[]>>(`${this.apiUrl}/participant/${participantId}`);
  }

  /**
   * Obtener excedentes disponibles por participante
   */
  getAvailableByParticipant(participantId: number): Observable<Result<ExcessPaymentResponseDTO[]>> {
    return this.http.get<Result<ExcessPaymentResponseDTO[]>>(`${this.apiUrl}/participant/${participantId}/available`);
  }

  /**
   * Obtener excedentes por estado
   */
  getByStatus(status: string): Observable<Result<ExcessPaymentResponseDTO[]>> {
    return this.http.get<Result<ExcessPaymentResponseDTO[]>>(`${this.apiUrl}/status/${status}`);
  }

  /**
   * Obtener excedentes disponibles para devolución
   */
  getAvailableForRefund(): Observable<Result<ExcessPaymentResponseDTO[]>> {
    return this.http.get<Result<ExcessPaymentResponseDTO[]>>(`${this.apiUrl}/available-for-refund`);
  }

  /**
   * Obtener excedentes por proyecto
   */
  getByProject(projectId: number): Observable<Result<ExcessPaymentResponseDTO[]>> {
    return this.http.get<Result<ExcessPaymentResponseDTO[]>>(`${this.apiUrl}/project/${projectId}`);
  }

  /**
   * Obtener resumen financiero completo de un grupo de pagos
   */
  getPaymentGroupSummary(paymentGroupId: string): Observable<Result<PaymentGroupSummaryDTO>> {
    return this.http.get<Result<PaymentGroupSummaryDTO>>(`${this.apiUrl}/payment-group/${paymentGroupId}/summary`);
  }
}